import AsyncSelect from 'react-select/async';
import { MultiValue } from 'react-select';
import { useRevalidator } from 'react-router-dom';
import RecommendConceptsClient from '../../clientAPI/recommendConcepts';
import * as R from 'ramda';
import { useState, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';

type ConceptSelectOptions = {
  value: any | undefined;
  label: string;
};

type ConceptIdsProps = {
  conceptIds: string[] | undefined;
  onChange: (newSelectOpts: MultiValue<{ label: string; value: any }>) => void;
  selectConceptsRef: any;
  validationError: string;
  searchableOnly: boolean | undefined;
};

export default function ConceptSelect(props: ConceptIdsProps) {
  const [selectedConceptOpts, setSelectedConceptOpts] = useState<
    ConceptSelectOptions[]
  >([]);

  const onChange = props.onChange;

  const rankingConcepts = props.conceptIds || [];

  const revalidator = useRevalidator();

  const searchableOnly = props.searchableOnly ?? true;

  const handleSelectChange = (
    newSelectOpts: MultiValue<{ label: string; value: any }>,
  ) => {
    const selectedConceptOpts = R.uniqBy(
      (t) => t.value.id,
      newSelectOpts.map((opt) => ({
        value: opt.value,
        label: opt.label,
      })),
    );
    setSelectedConceptOpts(selectedConceptOpts);
    onChange(newSelectOpts);
  };

  const asyncOptions = useDebouncedCallback((inputValue, callback) => {
    if (!inputValue) {
      callback([]);
      return;
    }

    // Check if input contains comma-separated IDs or looks like a UUID
    const uuidPattern =
      /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
    const trimmedInput = inputValue.trim();

    // Check if input contains commas (multiple IDs)
    const containsCommas = trimmedInput.includes(',');
    const potentialIds = containsCommas
      ? trimmedInput
          .split(',')
          .map((id: string) => id.trim())
          .filter((id: string) => uuidPattern.test(id))
      : uuidPattern.test(trimmedInput)
      ? [trimmedInput]
      : [];

    const promises: Promise<any>[] = [];

    // Always do a text search unless it's clearly just IDs
    if (!containsCommas || potentialIds.length === 0) {
      promises.push(
        RecommendConceptsClient.searchConcepts(
          inputValue,
          searchableOnly,
        ).catch(() => ({ objects: [] })),
      );
    } else {
      promises.push(Promise.resolve({ objects: [] }));
    }

    // If we have valid UUIDs, fetch them by ID
    if (potentialIds.length > 0) {
      const idFetches = potentialIds.map((id: string) =>
        RecommendConceptsClient.getConceptById(id).catch(() => null),
      );
      promises.push(Promise.all(idFetches));
    } else {
      promises.push(Promise.resolve([]));
    }

    Promise.all(promises)
      .then((results) => {
        const searchResults = results[0];
        const idResults = results[1] || [];

        const opts: any[] = [];

        // Add search results
        if (searchResults && searchResults.objects) {
          searchResults.objects.forEach((c: any) => {
            const label =
              c.name === c.alternateName
                ? `${c.name} ${c.source.system}`
                : `${c.name}(${c.alternateName}) ${c.source.system}`;
            opts.push({
              value: {
                id: c.id,
                name: c.name,
                navigationId: c.navigationId,
              },
              label,
            });
          });
        }

        // Add ID-based results
        if (Array.isArray(idResults)) {
          idResults.forEach((concept: any) => {
            if (concept) {
              const label =
                concept.name === concept.alternateName
                  ? `${concept.name} ${concept.source.system}`
                  : `${concept.name}(${concept.alternateName}) ${concept.source.system}`;

              // Check if it's not already in the results
              const alreadyExists = opts.some(
                (opt: any) => opt.value.id === concept.id,
              );
              if (!alreadyExists) {
                opts.unshift({
                  value: {
                    id: concept.id,
                    name: concept.name,
                    navigationId: concept.navigationId,
                  },
                  label: `${label} [ID Match]`,
                });
              }
            }
          });
        }

        callback(opts);
      })
      .catch((err: keyable) => {
        console.log(err);
        err.status === 401 && revalidator.revalidate();
        callback([]);
      });
  }, 1000);

  useEffect(() => {
    const conceptQueries = (rankingConcepts as unknown as string[]).map((c) =>
      RecommendConceptsClient.getConceptById(c).catch((err) => {
        console.log(err);
        err.status === 401 && revalidator.revalidate();
        return null;
      }),
    );
    Promise.all(conceptQueries).then((result) => {
      const opts = result.map((data, idx) => {
        if (data) {
          const label =
            data?.name === data?.alternateName
              ? `${data?.name} ${data?.source.system}`
              : `${data?.name}(${data?.alternateName}) ${data?.source.system}`;
          return {
            value: data,
            label: label,
          } as ConceptSelectOptions;
        } else {
          return {
            value: {
              id: rankingConcepts[idx],
              name: rankingConcepts[idx],
            },
            label: rankingConcepts[idx],
          };
        }
      });
      setSelectedConceptOpts(opts);
    });
  }, [rankingConcepts]);
  return (
    <>
      <div className='eds-select'>
        <AsyncSelect
          className='eds-text-field--select tw-h-30 tw-w-[35rem]'
          classNamePrefix='eds-select'
          isMulti
          cacheOptions
          value={selectedConceptOpts}
          loadOptions={asyncOptions}
          onChange={handleSelectChange}
          ref={props.selectConceptsRef}
        />
        {props.validationError && (
          <p
            className='tw-text-red-900'
            style={{ color: 'var(--eds-color-text-danger)' }}
          >
            {props.validationError}
          </p>
        )}
      </div>
    </>
  );
}
